import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLang } from '../contexts/LangContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, ArrowLeft, Mail, RefreshCw, Clock, UserCheck } from 'lucide-react';

export default function EmployeePage() {
  const { storeId } = useParams();
  const { t } = useLang();
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [invites, setInvites] = useState([]);
  const [storeName, setStoreName] = useState('');
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'staff', store_id: storeId });
  const [editForm, setEditForm] = useState({ name: '', role: 'staff', store_id: storeId });
  const [deleteId, setDeleteId] = useState(null);
  const [deleteType, setDeleteType] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => { loadData(); }, [storeId]);

  const loadData = async () => {
    try {
      const [empRes, storeRes, storesRes, invitesRes] = await Promise.all([
        api.listStoreEmployees(storeId),
        api.getStore(storeId),
        api.listStores(),
        api.listInvites(storeId)
      ]);
      setEmployees(empRes.data);
      setStoreName(storeRes.data.name);
      setStores(storesRes.data.data || []);
      setInvites(invitesRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await api.createInvite({ ...inviteForm, store_id: storeId });
      if (res.data.email_sent) {
        toast.success('Undangan berhasil dikirim via email!');
      } else {
        toast.warning('Undangan dibuat tapi email gagal terkirim. Anda bisa kirim ulang.');
      }
      setInviteDialogOpen(false);
      setInviteForm({ email: '', role: 'staff', store_id: storeId });
      loadData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('error'));
    } finally {
      setSending(false);
    }
  };

  const handleResendInvite = async (inviteId) => {
    try {
      const res = await api.resendInvite(inviteId);
      if (res.data.email_sent) {
        toast.success('Email undangan dikirim ulang!');
      } else {
        toast.error('Gagal mengirim ulang email');
      }
    } catch (err) {
      toast.error(t('error'));
    }
  };

  const handleEditEmployee = (emp) => {
    setEditing(emp);
    setEditForm({ name: emp.name, role: emp.role, store_id: emp.store_id || storeId });
    setEditDialogOpen(true);
  };

  const handleUpdateEmployee = async (e) => {
    e.preventDefault();
    try {
      await api.updateEmployee(editing.id, editForm);
      toast.success(t('success'));
      setEditDialogOpen(false);
      setEditing(null);
      loadData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('error'));
    }
  };

  const handleDelete = async () => {
    try {
      if (deleteType === 'employee') {
        await api.deleteEmployee(deleteId);
      } else {
        await api.cancelInvite(deleteId);
      }
      toast.success(t('success'));
      setDeleteId(null);
      setDeleteType('');
      loadData();
    } catch (err) { toast.error(t('error')); }
  };

  const roleBadge = (role) => {
    const colors = {
      operator: 'bg-blue-100 text-blue-700 border-blue-200',
      warehouse: 'bg-amber-100 text-amber-700 border-amber-200',
      staff: 'bg-green-100 text-green-700 border-green-200'
    };
    return <Badge className={`${colors[role] || 'bg-gray-100 text-gray-700'} text-xs border`}>{t(role)}</Badge>;
  };

  const statusBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      accepted: 'bg-green-100 text-green-700 border-green-200',
      expired: 'bg-red-100 text-red-700 border-red-200'
    };
    const labels = { pending: 'Menunggu', accepted: 'Diterima', expired: 'Kedaluwarsa' };
    return <Badge className={`${colors[status] || ''} text-xs border`}>{labels[status] || status}</Badge>;
  };

  const pendingInvites = invites.filter(i => i.status === 'pending');

  return (
    <div className="space-y-6 animate-fade-in" data-testid="employee-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/app/stores')} data-testid="back-to-stores-btn">
            <ArrowLeft size={18} />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-[#202020]" style={{ fontFamily: 'Manrope' }}>{t('employees')}</h1>
            <p className="text-sm text-[#64584F]">{storeName} - {employees.length} {t('employees').toLowerCase()}</p>
          </div>
        </div>
        <Button
          onClick={() => { setInviteForm({ email: '', role: 'staff', store_id: storeId }); setInviteDialogOpen(true); }}
          className="bg-[#EB7B44] hover:bg-[#D96A35] text-white font-medium"
          data-testid="invite-employee-btn"
        >
          <Mail size={16} className="mr-2" /> Undang Karyawan
        </Button>
      </div>

      <Tabs defaultValue="employees" className="w-full">
        <TabsList className="bg-[#DFDEDE] border border-[#CACACC]">
          <TabsTrigger value="employees" className="data-[state=active]:bg-white data-[state=active]:text-[#202020]" data-testid="tab-employees">
            <UserCheck size={14} className="mr-1.5" /> {t('employees')} ({employees.length})
          </TabsTrigger>
          <TabsTrigger value="invites" className="data-[state=active]:bg-white data-[state=active]:text-[#202020]" data-testid="tab-invites">
            <Clock size={14} className="mr-1.5" /> Undangan ({pendingInvites.length})
          </TabsTrigger>
        </TabsList>

        {/* Active Employees Tab */}
        <TabsContent value="employees">
          <Card className="bg-white border-[#CACACC] rounded-xl shadow-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F6F5F5] border-b border-[#CACACC]">
                  <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider">{t('name')}</TableHead>
                  <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider hidden sm:table-cell">{t('email')}</TableHead>
                  <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider">{t('role')}</TableHead>
                  <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider text-right">{t('actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={4} className="text-center py-12 text-[#A6A2A0]">{t('loading')}</TableCell></TableRow>
                ) : employees.length === 0 ? (
                  <TableRow><TableCell colSpan={4} className="text-center py-12 text-[#A6A2A0]">
                    <div className="space-y-2">
                      <p>{t('no_data')}</p>
                      <p className="text-xs">Klik "Undang Karyawan" untuk mengundang via email</p>
                    </div>
                  </TableCell></TableRow>
                ) : employees.map(emp => (
                  <TableRow key={emp.id} className="border-b border-[#DFDEDE] hover:bg-[#F6F5F5]/50 transition-colors" data-testid={`employee-row-${emp.id}`}>
                    <TableCell className="font-medium text-[#202020]">{emp.name}</TableCell>
                    <TableCell className="text-[#64584F] hidden sm:table-cell">{emp.email}</TableCell>
                    <TableCell>{roleBadge(emp.role)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleEditEmployee(emp)} data-testid={`employee-edit-btn-${emp.id}`}>
                          <Pencil size={15} className="text-[#64584F]" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => { setDeleteId(emp.id); setDeleteType('employee'); }} data-testid={`employee-delete-btn-${emp.id}`}>
                          <Trash2 size={15} className="text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Pending Invites Tab */}
        <TabsContent value="invites">
          <Card className="bg-white border-[#CACACC] rounded-xl shadow-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F6F5F5] border-b border-[#CACACC]">
                  <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider">{t('email')}</TableHead>
                  <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider">{t('role')}</TableHead>
                  <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider hidden sm:table-cell">{t('status')}</TableHead>
                  <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider hidden md:table-cell">{t('date')}</TableHead>
                  <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider text-right">{t('actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invites.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-12 text-[#A6A2A0]">Belum ada undangan</TableCell></TableRow>
                ) : invites.map(inv => (
                  <TableRow key={inv.id} className="border-b border-[#DFDEDE] hover:bg-[#F6F5F5]/50 transition-colors" data-testid={`invite-row-${inv.id}`}>
                    <TableCell className="font-medium text-[#202020]">{inv.email}</TableCell>
                    <TableCell>{roleBadge(inv.role)}</TableCell>
                    <TableCell className="hidden sm:table-cell">{statusBadge(inv.status)}</TableCell>
                    <TableCell className="text-[#64584F] text-sm hidden md:table-cell">{inv.created_at?.split('T')[0]}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {inv.status === 'pending' && (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => handleResendInvite(inv.id)} title="Kirim ulang" data-testid={`invite-resend-btn-${inv.id}`}>
                              <RefreshCw size={15} className="text-[#EB7B44]" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => { setDeleteId(inv.id); setDeleteType('invite'); }} data-testid={`invite-cancel-btn-${inv.id}`}>
                              <Trash2 size={15} className="text-red-500" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Invite Dialog */}
      <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
        <DialogContent className="bg-white border-[#CACACC] rounded-xl max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#202020]" style={{ fontFamily: 'Manrope' }}>
              <Mail size={18} className="inline mr-2 text-[#EB7B44]" />
              Undang Karyawan
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleInvite} className="space-y-4">
            <div className="bg-[#F6F5F5] rounded-lg p-3">
              <p className="text-xs text-[#64584F]">Karyawan akan menerima email undangan untuk membuat akun dan bergabung di toko <strong>{storeName}</strong>.</p>
            </div>
            <div className="space-y-2">
              <Label className="text-[#202020]">Email Karyawan</Label>
              <Input
                type="email"
                value={inviteForm.email}
                onChange={e => setInviteForm({...inviteForm, email: e.target.value})}
                required
                placeholder="karyawan@email.com"
                className="border-[#CACACC] focus:border-[#EB7B44]"
                data-testid="invite-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#202020]">{t('role')}</Label>
              <Select value={inviteForm.role} onValueChange={val => setInviteForm({...inviteForm, role: val})}>
                <SelectTrigger className="border-[#CACACC]" data-testid="invite-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="operator">{t('operator')}</SelectItem>
                  <SelectItem value="warehouse">{t('warehouse')}</SelectItem>
                  <SelectItem value="staff">{t('staff')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setInviteDialogOpen(false)} className="border-[#CACACC]">{t('cancel')}</Button>
              <Button type="submit" disabled={sending} className="bg-[#EB7B44] hover:bg-[#D96A35] text-white" data-testid="send-invite-btn">
                {sending ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> : <Mail size={14} className="mr-2" />}
                Kirim Undangan
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Employee Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-white border-[#CACACC] rounded-xl max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#202020]" style={{ fontFamily: 'Manrope' }}>{t('edit')} {t('employees')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateEmployee} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[#202020]">{t('name')}</Label>
              <Input value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} required className="border-[#CACACC]" data-testid="edit-employee-name-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-[#202020]">{t('role')}</Label>
              <Select value={editForm.role} onValueChange={val => setEditForm({...editForm, role: val})}>
                <SelectTrigger className="border-[#CACACC]" data-testid="edit-employee-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="operator">{t('operator')}</SelectItem>
                  <SelectItem value="warehouse">{t('warehouse')}</SelectItem>
                  <SelectItem value="staff">{t('staff')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#202020]">{t('store')}</Label>
              <Select value={editForm.store_id} onValueChange={val => setEditForm({...editForm, store_id: val})}>
                <SelectTrigger className="border-[#CACACC]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {stores.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)} className="border-[#CACACC]">{t('cancel')}</Button>
              <Button type="submit" className="bg-[#EB7B44] hover:bg-[#D96A35] text-white" data-testid="save-employee-btn">{t('save')}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteId} onOpenChange={() => { setDeleteId(null); setDeleteType(''); }}>
        <DialogContent className="bg-white border-[#CACACC] rounded-xl max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-[#202020]">
              {deleteType === 'invite' ? 'Batalkan undangan ini?' : t('confirm_delete')}
            </DialogTitle>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteId(null)} className="border-[#CACACC]">{t('cancel')}</Button>
            <Button onClick={handleDelete} className="bg-red-500 hover:bg-red-600 text-white" data-testid="confirm-delete-employee-btn">
              {deleteType === 'invite' ? 'Batalkan' : t('delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
